/**
 * Dicionário PT → EN da pré-consulta.
 *
 * O questionário é escrito em português no código (fonte canônica, igual ao
 * texto validado do formulário). Este módulo traduz rótulos, opções e textos
 * de apoio na hora de exibir/exportar, sem alterar os valores gravados no
 * banco — as respostas continuam canônicas em português para as estatísticas.
 */

export type PreconsultaLocale = "pt-BR" | "en";

export const PRECONSULTA_LOCALES: { value: PreconsultaLocale; label: string }[] = [
  { value: "pt-BR", label: "Português (Brasil)" },
  { value: "en", label: "English" },
];

/** Idiomas que o paciente pode solicitar quando não fala português nem inglês. */
export const REQUESTABLE_LANGUAGES = [
  "Español",
  "Français",
  "Italiano",
  "Deutsch",
  "العربية (Árabe)",
  "עברית (Hebraico)",
  "中文 (Mandarim)",
  "Outro / Other",
] as const;

const EN: Record<string, string> = {
  // ------------------------------------------------------------- chrome
  "Pré-consulta médica": "Medical pre-consultation",
  "Etapa": "Step",
  "de": "of",
  "Progresso do questionário": "Questionnaire progress",
  "Voltar": "Back",
  "Continuar": "Continue",
  "Enviar": "Submit",
  "Salvando…": "Saving…",
  "Respostas salvas": "Answers saved",
  "Editar": "Edit",
  "Remover": "Remove",
  "+ Adicionar": "+ Add",
  "+ Adicionar outra cirurgia": "+ Add another surgery",
  "Carregando": "Loading",
  "Preencha os campos obrigatórios para continuar.":
    "Please complete the required fields to continue.",
  "Para continuar, preencha:": "To continue, please complete:",
  "Ainda faltam respostas obrigatórias:": "Some required answers are still missing:",
  "Não foi possível enviar. Tente novamente.": "We couldn’t submit your answers. Please try again.",
  "Estas respostas vieram do seu questionário anterior. Confira se continuam corretas — se algo mudou, é só editar.":
    "These answers come from your previous questionnaire. Please check whether they are still correct — if anything changed, just edit it.",
  "Está tudo igual, continuar": "Everything is the same, continue",
  "As informações enviadas são confidenciais e usadas exclusivamente para o seu atendimento médico, conforme a LGPD. Elas não substituem uma consulta nem constituem diagnóstico.":
    "The information you send is confidential and used only for your medical care, under Brazilian data protection law (LGPD). It does not replace a consultation and is not a diagnosis.",
  "Declaro que as informações prestadas são verdadeiras e completas, de acordo com o meu conhecimento.":
    "I declare that the information provided is true and complete to the best of my knowledge.",
  "Versão": "Version",
  "Pré-consulta enviada": "Pre-consultation submitted",
  "Obrigado. Suas respostas foram registradas com segurança e serão revisadas pelo Dr. Fernando Amato antes do seu atendimento. Você não precisa fazer mais nada agora.":
    "Thank you. Your answers were recorded securely and will be reviewed by Dr. Fernando Amato before your appointment. There is nothing else you need to do now.",

  // -------------------------------------------------------- seções/títulos
  "Identificação": "Identification",
  "Desde a última vez": "Since your last visit",
  "Motivo da consulta": "Reason for the consultation",
  "Medidas": "Measurements",
  "Histórico médico": "Medical history",
  "Medicamentos": "Medications",
  "Cirurgias prévias": "Previous surgeries",
  "Percepção corporal e expectativas": "Body perception and expectations",
  "Mamas — qualidade de vida (BREAST-Q)": "Breasts — quality of life (BREAST-Q)",
  "Mamas — qualidade de vida": "Breasts — quality of life",
  "Estilo de vida": "Lifestyle",
  "Lipedema": "Lipedema",
  "Rede de apoio e recuperação": "Support network and recovery",
  "Planejamento": "Planning",
  "Informações finais": "Final information",
  "Revisão": "Review",
  "Confira suas respostas antes de enviar. Você pode editar qualquer seção.":
    "Check your answers before submitting. You can edit any section.",

  // ------------------------------------------------------ identificação
  "Nome completo": "Full name",
  "E-mail": "E-mail",
  "Telefone/celular com DDD": "Phone / mobile with area code",
  "Data de nascimento": "Date of birth",
  "Idade calculada": "Calculated age",
  "anos": "years",
  "Profissão": "Occupation",
  "Grau de escolaridade": "Education level",
  "Com qual profissional da equipe você vai se consultar?":
    "Which member of the team will see you?",
  "Nome do profissional": "Name of the professional",
  "Como chegou até o Dr. Fernando Amato?": "How did you find Dr. Fernando Amato?",
  "Qual?": "Which one?",
  "Médio incompleto": "Incomplete high school",
  "Médio completo": "High school",
  "Superior incompleto": "Incomplete undergraduate",
  "Superior completo": "Undergraduate degree",
  "Pós graduação": "Postgraduate",
  "Não Sabe": "Don’t know",
  "Dr. Fernando Amato": "Dr. Fernando Amato",
  "Dra. Bianca Nunes": "Dr. Bianca Nunes",
  "Vários profissionais - Lipedema": "Multiple professionals - Lipedema",
  "Ainda não sei / a equipe indicou o horário":
    "I don’t know yet / the team scheduled the appointment",
  "Indicação de médico": "Referred by a doctor",
  "Indicação de paciente": "Referred by a patient",
  "Paciente antigo": "Former patient",
  "Outro": "Other",
  "Outra": "Other",

  // ------------------------------------------------------------ motivo
  "Qual região do corpo fez você procurar tratamento?":
    "Which area of your body made you look for treatment?",
  "Você pode escolher mais de uma.": "You can choose more than one.",
  "Qual outra região?": "Which other area?",
  "Descreva o tratamento ou cirurgia que procura.":
    "Describe the treatment or surgery you are looking for.",
  "O que mais incomoda você atualmente?": "What bothers you the most right now?",
  "Há quanto tempo isso incomoda?": "How long has it been bothering you?",
  "Se pudesse melhorar apenas uma coisa, qual seria?":
    "If you could improve only one thing, what would it be?",
  "O que você espera alcançar com o tratamento ou cirurgia?":
    "What do you hope to achieve with the treatment or surgery?",
  "Qual é sua maior preocupação em relação ao tratamento ou cirurgia?":
    "What is your biggest concern about the treatment or surgery?",
  "Face (orelha, pálpebras, nariz, etc)": "Face (ear, eyelids, nose, etc.)",
  "Face Lateral ( nariz, queixo, boca, orelha, etc)":
    "Side of the face (nose, chin, mouth, ear, etc.)",
  "corpo - masculino": "Body — male",
  "Corpo feminino - anterior (abdomen, região íntima, mamas, etc)":
    "Female body — front (abdomen, intimate area, breasts, etc.)",
  "corpo feminino - posterior ( Dorso, gluteo)": "Female body — back (back, buttocks)",
  "Torax anterior ( mamas, abdomen, gluteo) etc)": "Front of the trunk (breasts, abdomen, buttocks)",
  "Pernas": "Legs",

  // ------------------------------------------------------------ medidas
  "Usadas pelo Dr. Fernando durante a avaliação.": "Used by Dr. Fernando during the assessment.",
  "Altura (cm)": "Height (cm)",
  "Peso atual (kg)": "Current weight (kg)",
  "Maior peso que já teve na vida (kg)": "Highest weight you have ever had (kg)",
  "Peso há 6 meses (kg)": "Weight 6 months ago (kg)",
  "Peso há 12 meses (kg)": "Weight 12 months ago (kg)",
  "Houve perda ou ganho recente de peso?": "Have you recently lost or gained weight?",
  "Opcional.": "Optional.",
  "Opcional. Se sim, quanto e em quanto tempo.": "Optional. If so, how much and over what period.",
  "IMC registrado": "Recorded BMI",

  // --------------------------------------------------------- histórico
  "Sim": "Yes",
  "Não": "No",
  "Não sei": "I don’t know",
  "Não sabe": "Doesn’t know",
  "Diabetes?": "Diabetes?",
  "Como trata o diabetes?": "How do you treat your diabetes?",
  "Usa insulina?": "Do you use insulin?",
  "Sim, uso insulina": "Yes, I use insulin",
  "Última hemoglobina glicada (HbA1c), se souber":
    "Last glycated haemoglobin (HbA1c), if you know it",
  "Hipertensão arterial?": "High blood pressure?",
  "Faz tratamento? Qual?": "Are you being treated? How?",
  "Tabagismo?": "Smoking?",
  "Nunca fumei": "Never smoked",
  "Fumo atualmente": "I currently smoke",
  "Ex-fumante": "Former smoker",
  "Prefiro não informar": "Prefer not to say",
  "Quantidade aproximada por dia": "Approximate amount per day",
  "Há quanto tempo fuma?": "How long have you been smoking?",
  "Quando parou de fumar?": "When did you stop smoking?",
  "Alterações da tiróide?": "Thyroid disorders?",
  "Hipertiroidismo": "Hyperthyroidism",
  "Hipotireoidismo": "Hypothyroidism",
  "Já teve trombose?": "Have you ever had thrombosis?",
  "Alguem da família já teve?": "Someone in my family has had it",
  "Descreva brevemente": "Briefly describe",
  "Quem e em que situação?": "Who, and in what situation?",
  "Dificuldade de cicatrização?": "Difficulty healing?",
  "Descreva": "Describe",
  "Anemia?": "Anaemia?",
  "Depressão, síndrome do pânico, ansiedade ou outra condição psiquiátrica?":
    "Depression, panic disorder, anxiety or another psychiatric condition?",
  "Descreva e informe se faz acompanhamento":
    "Describe it and tell us whether you are in treatment",
  "Alergias?": "Allergies?",
  "A quê? (medicamentos, alimentos, materiais…)": "To what? (medicines, foods, materials…)",
  "Outras doenças?": "Other conditions?",
  "Quais?": "Which ones?",

  // ------------------------------------------------------- medicamentos
  "Liste o que usa atualmente. Se não usar nada em alguma categoria, deixe em branco.":
    "List what you currently take. Leave a category blank if it does not apply.",
  "Medicamentos em uso": "Medicines in use",
  "Hormônios": "Hormones",
  "Vitaminas": "Vitamins",
  "Suplementos": "Supplements",
  "Medicamentos para emagrecimento ou controle de peso": "Weight-loss or weight-control medicines",
  "Ex.: Mounjaro/tirzepatida, Ozempic/Wegovy/semaglutida, Saxenda/liraglutida, entre outros.":
    "E.g. Mounjaro/tirzepatide, Ozempic/Wegovy/semaglutide, Saxenda/liraglutide, among others.",
  "Nome": "Name",
  "Dose, se souber": "Dose, if you know it",
  "Frequência": "Frequency",
  "Última utilização": "Last time you took it",
  "Preencha quando não for de uso contínuo.": "Fill in when it is not taken continuously.",

  // ---------------------------------------------------------- cirurgias
  "Já fez alguma cirurgia?": "Have you had any surgery?",
  "Procedimento": "Procedure",
  "Ano ou data aproximada": "Year or approximate date",
  "Houve alguma complicação?": "Were there any complications?",

  // ------------------------------------------------------ saúde mental
  "Estas respostas ajudam o Dr. Fernando a entender como você se percebe. Não há respostas certas ou erradas.":
    "These answers help Dr. Fernando understand how you see yourself. There are no right or wrong answers.",
  "Os itens da escala validada ainda não foram carregados no sistema. Esta seção será preenchida assim que o conteúdo oficial do questionário for cadastrado.":
    "This validated scale is only available in Portuguese and is therefore not part of the English questionnaire. Dr. Fernando will go through these questions with you during the appointment.",

  // -------------------------------------------------------------- mamas
  "Estas perguntas fazem parte de um questionário internacional validado (BREAST-Q). Não há respostas certas ou erradas — elas ajudam a medir o impacto das queixas na sua qualidade de vida.":
    "These questions are part of an internationally validated questionnaire (BREAST-Q). There are no right or wrong answers — they help measure how your concerns affect your quality of life.",
  "Estas perguntas avaliam como você está depois da cirurgia e a sua experiência com o atendimento. Elas permitem comparar o antes e o depois.":
    "These questions assess how you are doing after surgery and your experience with our care. They allow us to compare before and after.",
  "Os itens do BREAST-Q ainda não foram carregados no sistema. Esta seção será preenchida assim que o conteúdo oficial for cadastrado.":
    "The official English BREAST-Q items are not loaded in the system yet. Dr. Fernando will review these questions with you during the appointment.",

  // -------------------------------------------------------- estilo de vida
  "Como você classifica sua alimentação?": "How would you rate your diet?",
  "MUITO BOA (regular, balanceada, variada)": "VERY GOOD (regular, balanced, varied)",
  "RUIM (Irregular, poucas frutas, alimentos industrializados, fritura)":
    "POOR (irregular, little fruit, processed food, fried food)",
  "Descreva brevemente sua alimentação": "Briefly describe your diet",
  "Como você classifica seu hábito intestinal?": "How would you rate your bowel habits?",
  "MUITO BOM (regular, todo dia, horário)": "VERY GOOD (regular, every day, same time)",
  "RUIM (Irregular, períodos de constipação ou diarréia)":
    "POOR (irregular, periods of constipation or diarrhoea)",
  "Faz alguma dieta, qual?": "Do you follow any diet? Which one?",
  'Se não faz, escreva "Não".': "If you don’t, write “No”.",
  "Como você classifica seu sono?": "How would you rate your sleep?",
  "MUITO BOM (Não acorda a noite, sono reparador)":
    "VERY GOOD (no night awakenings, restorative sleep)",
  "RUIM (várias vezes a noite, dorme pouco, dificuldade de dormir)":
    "POOR (wake up several times, sleep little, hard to fall asleep)",
  "Quantas horas dorme por dia?": "How many hours do you sleep per day?",
  "mais que 9 horas": "more than 9 hours",
  "entre 7 e 9 horas": "between 7 and 9 hours",
  "entre 5 e 7 horas": "between 5 and 7 hours",
  "menos que 5 horas": "less than 5 hours",
  "Fica noites sem dormir": "Sometimes I don’t sleep at all",
  "Quantas vezes acorda à noite?": "How many times do you wake up at night?",
  "não acorda": "I don’t wake up",
  "1 vez": "once",
  "2 vezes": "twice",
  "3 vezes": "3 times",
  "Mais que 4 vezes": "more than 4 times",
  "Você faz atividade física?": "Do you exercise?",
  "SIM, faz atividade física regularmente": "YES, I exercise regularly",
  "NÃO faz nehuma atividade, é sedentário": "NO, I don’t exercise at all (sedentary)",
  "Quantas vezes faz atividade física por semana?": "How many times a week do you exercise?",
  "Não faz": "I don’t",
  "1 a 2 vezes por semana": "1 to 2 times a week",
  "3 vezes por semana": "3 times a week",
  "4 a 5 vezes por semana": "4 to 5 times a week",
  "6 a 7 vezes por semana": "6 to 7 times a week",
  "Quanto tempo de atividade física realiza por vez?": "How long is each exercise session?",
  "Mais que 120 min": "more than 120 min",
  "60 a 120 min": "60 to 120 min",
  "30 a 60 min": "30 to 60 min",
  "Até 30 min": "up to 30 min",
  "Faz uso de bebida alcoólica?": "Do you drink alcohol?",
  "Respostas de 2 a 4 são subjetivas à frequência do uso.":
    "Answers 2 to 4 reflect how often you use it.",
  "Respostas de 2 a 4 são subjetivas à frequência do uso. Esta informação é sigilosa e importante para a segurança da anestesia.":
    "Answers 2 to 4 reflect how often you use it. This information is confidential and important for anaesthetic safety.",
  "Não, nunca usou": "No, never used",
  "Sim, diariamente": "Yes, daily",
  "Faz uso de medicação para dormir?": "Do you take sleeping medication?",
  "Faz uso de estimulantes?": "Do you use stimulants?",
  "Faz uso de drogas ilícitas?": "Do you use recreational drugs?",
  "Como você classifica o estresse na sua vida?": "How would you rate the stress in your life?",
  "SEM ESTRESSE": "NO STRESS",
  "MUITO ESTRESSADO": "VERY STRESSED",
  "Como são seus relacionamentos?": "How are your relationships?",
  "Muitos amigos, bons relacionamentos, satisfeito":
    "Many friends, good relationships, satisfied",
  "Poucos amigos, prefere ficar sozinho, possui atuais amizades desgastantes e tóxicas":
    "Few friends, prefer to be alone, current relationships are draining or toxic",
  "Tem dor de cabeça, cefaléia ou enxaqueca?": "Do you get headaches or migraines?",
  "Nunca": "Never",
  "Todos os dias": "Every day",
  "Faz uso de medicamentos sintomáticos? Analgésicos para dor (dipirona, paracetamol, ibuprofeno, etc..) Antieméticos para enjoo (dramin, plasil, ondansetrona)?":
    "Do you take symptom-relief medicines? Painkillers (dipyrone, paracetamol, ibuprofen, etc.) or anti-nausea drugs (dimenhydrinate, metoclopramide, ondansetron)?",
  "Somente quando é prescrito, quando estou doente": "Only when prescribed, when I am ill",
  "As vezes": "Sometimes",
  "Frequentemente": "Often",
  "Quase sempre": "Almost always",

  // ----------------------------------------------------------- lipedema
  "Você tem ou acha que tem LIPEDEMA?": "Do you have, or think you have, LIPEDEMA?",
  "Questionário Lipedema": "Lipedema questionnaire",
  "Os itens do QuASiL ainda não foram carregados no sistema. Esta seção será preenchida assim que o conteúdo validado for cadastrado.":
    "This validated lipedema questionnaire exists only in Portuguese, so it is not part of the English version. Dr. Fernando will cover it during the appointment.",
  "Perguntas Lipedema extra": "Additional lipedema questions",
  "Já fez lipoaspiração prévia? pode responder mais de 1 opção":
    "Have you had liposuction before? You can select more than one option",
  "Coxa / culote": "Thigh / saddlebags",
  "Coxa/ face interna": "Thigh / inner side",
  "Braços": "Arms",
  "Ao redor dos joelhos": "Around the knees",
  "Outras regioes do corpo (abdomen e dorso)": "Other areas (abdomen and back)",

  // -------------------------------------------------------------- apoio
  "Se houver indicação de cirurgia, quem poderia ajudar você nos primeiros dias de recuperação?":
    "If surgery is indicated, who could help you during the first days of recovery?",
  "Quanto tempo aproximadamente conseguiria ficar afastado(a) do trabalho?":
    "Roughly how long could you stay away from work?",
  "Seu trabalho exige esforço físico?": "Does your work require physical effort?",
  "Trabalha principalmente:": "You work mostly:",
  "Sentado": "Sitting",
  "Em pé": "Standing",
  "Alternando": "Alternating",
  "Trabalho físico": "Physical work",
  "Home office": "From home",

  // -------------------------------------------------------- planejamento
  "Em que período imagina realizar o tratamento/cirurgia?":
    "When are you thinking of having the treatment/surgery?",
  "Assim que possível": "As soon as possible",
  "Até 3 meses": "Within 3 months",
  "3–6 meses": "3–6 months",
  "6–12 meses": "6–12 months",
  "Mais de 12 meses": "More than 12 months",
  "Ainda não sei": "I don’t know yet",

  // --------------------------------------------------------------- final
  "Existe alguma informação que considera importante e gostaria que o Dr. Fernando soubesse antes da consulta?":
    "Is there anything else you consider important and would like Dr. Fernando to know before the appointment?",

  // ------------------------------------------------------------ retorno
  "Você já foi atendida pelo Dr. Fernando Amato antes. Conte o que mudou desde então — assim evitamos repetir o que já sabemos.":
    "You have been seen by Dr. Fernando Amato before. Tell us what has changed since then — that way we don’t repeat what we already know.",
  "Quando foi o último atendimento? (mês/ano ou aproximado)":
    "When was your last appointment? (month/year or approximate)",
  "Você chegou a operar com a nossa equipe?": "Did you have surgery with our team?",
  "Quais procedimentos ou cirurgias você realizou desde então (com a nossa equipe ou não)?":
    "Which procedures or surgeries have you had since then (with our team or elsewhere)?",
  "Surgiu algum diagnóstico novo nesse período?": "Have you received any new diagnosis since then?",
  "Começou a usar alguma medicação nova?": "Have you started any new medication?",
  "Houve alguma complicação ou intercorrência na recuperação anterior?":
    "Were there any complications during your previous recovery?",
  "O que motivou o seu retorno agora?": "What brings you back now?",

  // ------------------------------------------------------------ revisão
  "Telefone": "Phone",
  "Idade": "Age",
  "Escolaridade": "Education",
  "Profissional do atendimento": "Attending professional",
  "Como chegou até o consultório": "How you found the practice",
  "Regiões": "Areas",
  "Outra região": "Other area",
  "Tratamento procurado": "Treatment sought",
  "O que mais incomoda": "Main complaint",
  "Há quanto tempo": "For how long",
  "Se pudesse melhorar uma coisa": "If you could improve one thing",
  "Expectativa": "Expectation",
  "Maior preocupação": "Biggest concern",
  "Altura": "Height",
  "Peso": "Weight",
  "IMC": "BMI",
  "Maior peso": "Highest weight",
  "Variação recente": "Recent change",
  "Diabetes": "Diabetes",
  "Tratamento do diabetes": "Diabetes treatment",
  "Insulina": "Insulin",
  "Hipertensão": "High blood pressure",
  "Tabagismo": "Smoking",
  "Tireoide": "Thyroid",
  "Trombose prévia": "Previous thrombosis",
  "Trombose na família": "Family history of thrombosis",
  "Dificuldade de cicatrização": "Difficulty healing",
  "Anemia": "Anaemia",
  "Condição psiquiátrica": "Psychiatric condition",
  "Alergias": "Allergies",
  "Outras doenças": "Other conditions",
  "Nenhum informado": "None reported",
  "Cirurgia": "Surgery",
  "Escore de estilo de vida": "Lifestyle score",
  "Escores BREAST-Q transformados (0–100; quanto maior, melhor).":
    "Transformed BREAST-Q scores (0–100; higher is better).",
  "Alimentação": "Diet",
  "Sono": "Sleep",
  "Horas de sono": "Hours of sleep",
  "Atividade física": "Physical activity",
  "Álcool": "Alcohol",
  "Estimulantes": "Stimulants",
  "Estresse": "Stress",
  "Cefaleia": "Headache",
  "Tem ou acha que tem lipedema": "Has or believes to have lipedema",
  "Lipoaspiração prévia": "Previous liposuction",
  "Regiões tratadas": "Treated areas",
  "Nível de dor": "Pain level",
  "Limitação para caminhar": "Walking limitation",
  "Qualidade de vida": "Quality of life",
  "Rede de apoio": "Support network",
  "Afastamento do trabalho": "Time off work",
  "Esforço físico no trabalho": "Physical effort at work",
  "Postura no trabalho": "Posture at work",
  "Período pretendido": "Intended timeframe",
  "Observações": "Notes",

  // --------------------------------------------- obrigatórios (validação)
  "Como conheceu o consultório": "How you found the practice",
  "Motivo do retorno": "Reason for coming back",
  "Quando foi o último atendimento": "Date of the last appointment",
  "Regiões de interesse": "Areas of interest",
  "Procedimento de interesse": "Procedure of interest",
  "Resultado esperado": "Expected outcome",
  "Acompanhamento psiquiátrico/psicológico": "Psychiatric/psychological follow-up",
  "Outras condições": "Other conditions",
  "Qualidade do sono": "Sleep quality",
  "Nível de estresse": "Stress level",
  "Quem poderá acompanhar você": "Who can support you",
  "Quando pretende realizar": "When you plan to have it",

  // --------------------------------------------------- consentimento/início
  "Consentimento para tratamento dos seus dados": "Consent to process your data",
  "Aceitar e começar": "Accept and start",
  "Li e concordo com o tratamento dos meus dados de saúde nas condições acima.":
    "I have read and agree to the processing of my health data under the conditions above.",
  "Termo versão": "Consent version",
  "Questionário não encontrado": "Questionnaire not found",
  "Este link não é válido ou expirou. Entre em contato com a equipe para receber um novo acesso.":
    "This link is invalid or has expired. Please contact the team to receive new access.",

  // ------------------------------------------------- resumo para prontuário
  "PRÉ-CONSULTA — ANAMNESE DIRIGIDA (relato do paciente)":
    "PRE-CONSULTATION — STRUCTURED HISTORY (patient-reported)",
  "Paciente": "Patient",
  "Data do preenchimento": "Date completed",
  "Preenchimento do questionário": "Questionnaire completion",
  "Informações relatadas pelo paciente em questionário eletrônico, sem verificação presencial. Documento clínico de uso interno (LGPD/CFM).":
    "Information reported by the patient through an electronic questionnaire, without in-person verification. Internal clinical document.",
  "Marcadores para revisão (uso interno)": "Flags for review (internal use)",
  "Nunca fumou": "Never smoked",
  "Fumante atual": "Current smoker",
};

/** Traduz um texto do português para o idioma do questionário. */
export function translate(locale: PreconsultaLocale, value: string): string {
  if (locale !== "en") return value;
  const trimmed = value.trim();
  return EN[trimmed] ?? value;
}

export function normalizeLocale(value: string | null | undefined): PreconsultaLocale {
  return value === "en" ? "en" : "pt-BR";
}

/** Escalas validadas só em português — omitidas no questionário em inglês. */
export const PT_ONLY_SCALE_CODES = ["BDSS_UNIFESP", "QUASIL"];
